import {
  AgendaSlotReservationApi,
  SlotReservationDoctorSubstitute,
  UnauthorizedError,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';
import {
  AgendaSlotItemReservation,
  getAgendaSlotItemSchema,
} from './use-get-office-agendas-slots';

export const getAgendaSlotByIdSchema = getAgendaSlotItemSchema;

export type GetAgendaSlotByIdResponse = z.infer<typeof getAgendaSlotByIdSchema>;

export const getOfficeAgendaSlotById = (id: string) => async () => {
  return http.agendaSlots
    .getById(id)
    .then<GetAgendaSlotByIdResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {
          startAt: true,
          endAt: true,
          periodOfTheDay: true,
          type: true,
          rrule: true,
          exdate: true,
          retrocessionRate: true,
          withAssistant: true,
          withAssistantInferred: true,
          sourceId: true,
          occurenceIndex: true,
          morningDoctolibDataCreatedAt: true,
          isFeeApplicable: true,
          workAtTheSameTime: true,
          createdAt: true,
          updatedAt: true,
          pendingRequestCount: true,
        },
        relations: {
          doctorAttending: true,
          reservations: ({
            entity,
          }: {
            entity: AgendaSlotReservationApi[];
          }): AgendaSlotItemReservation[] => {
            return entity.map((res) => {
              const doctorSubstitute =
                transform.getApiEntityRelation<SlotReservationDoctorSubstitute>(
                  {
                    relation: 'doctorSubstitute',
                    relationships: res.relationships,
                    included: response.included,
                  },
                );
              if (!doctorSubstitute) {
                return {
                  id: res.id,
                  status: res.attributes.status,
                  doctorSubstitute: {
                    id: '',
                    firstname: '',
                    lastname: '',
                    picture: null,
                    installable: false,
                    hasPhysicalSiteAccess: false,
                  },
                };
              }
              return {
                id: res.id,
                status: res.attributes.status,
                doctorSubstitute: {
                  id: doctorSubstitute.id,
                  firstname: doctorSubstitute.attributes.firstname,
                  lastname: doctorSubstitute.attributes.lastname,
                  picture: doctorSubstitute.attributes.picture ?? null,
                  priority: doctorSubstitute.attributes.priority ?? null,
                  installable: doctorSubstitute.attributes.installable,
                  hasPhysicalSiteAccess:
                    doctorSubstitute.attributes.hasPhysicalSiteAccess ?? false,
                },
              };
            });
          },
        },
        schema: getAgendaSlotByIdSchema,
      });

      // Zod schema defaults fill in pendingRequestCount and reservations at parse time
      return result as unknown as GetAgendaSlotByIdResponse;
    });
};

export const useGetOfficeAgendaSlotByIdQuery = (
  id: string,
  enabled: boolean,
): UseQueryResult<GetAgendaSlotByIdResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getOfficeAgendaSlotById(id),
    queryKey: ['office-agenda-slot-by-id', id],
    retry: false,
    enabled,
  });
};
